import React from 'react';
import { Redirect } from 'react-router-dom';
import { defaultMemoize } from 'reselect';
import _ from 'underscore';

import { ServerUrls } from '~commonInfra/url/ServerUrls';
import { Location } from '~commonInfra/utils/Location';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';

import { Messages } from '../../codegen/Localize';
import { ExtractTaskDefinition } from '../../extractTask/components/spaces/contentDefinitions/ExtractTaskDefinition';
import { RunFlowTaskDefinition } from '../../flow/contentDefinitions/RunFlowTaskDefinition';
import { explorerSubspace } from '../../spacesPlatform/components/spaceComponents/SubspaceHelpers';
import { Subspace, TabbedSpace } from '../../spacesPlatform/components/spaceComponents/TabbedSpace';
import { ISpaceInfoTypeItemCount } from '../../spacesPlatform/state/ContentCountsState';
import { ScheduleSubspaceName, SpaceInfo } from '../../spacesPlatform/state/SpaceInfo';
import { SubscriptionDefinition } from '../../subscription/contentDefinitions/SubscriptionDefinition';
import { ScheduleActions } from '../types/ISchedule';
import { IScheduleInfo } from '../types/IScheduleInfo';
import { schedulePath, scheduleSubspaceNameMatchesSchedule } from '../utils/ScheduleUtils';
import { ScheduleDetailsSubspace } from './ScheduleDetailsSubspace';
import { ScheduleSpaceHeader } from './ScheduleSpaceHeader';


export interface ScheduleSpaceStateProps {
  readonly spaceInfo: SpaceInfo;
  readonly contentCounts: ISpaceInfoTypeItemCount<'schedule'>;
  readonly contentCountsUpToDate: boolean;
  readonly displaySchedulesInClientTimezone: boolean;
}

export interface ScheduleSpaceDispatchProps {
  readonly fetchUpdatedContentCounts: () => void;
}

export interface ScheduleSpaceOwnProps {
  readonly urls: SpacesSiteUrls | ServerUrls;
  readonly reloadSpace: () => void;
}

export type ScheduleSpaceProps = ScheduleSpaceStateProps & ScheduleSpaceDispatchProps & ScheduleSpaceOwnProps;

export class ScheduleSpace extends React.PureComponent<ScheduleSpaceProps> {

  private memoizedGetSiteUserSubspaces = defaultMemoize(getScheduleSubspaces);

  public componentDidUpdate(): void {
    this.props.fetchUpdatedContentCounts();
  }

  public componentDidMount(): void {
    this.props.fetchUpdatedContentCounts();
  }

  private static scheduleUrlBySubspace: Record<ScheduleSubspaceName, (urls: SpacesSiteUrls | ServerUrls, scheduleId: string) => string> = {
    flows: (urls, scheduleId) => urls.scheduleFlowsUrl(scheduleId),
    subscriptions: (urls, scheduleId) => urls.scheduleSubscriptionsUrl(scheduleId),
    extractRefreshes: (urls, scheduleId) => urls.scheduleExtractRefreshesUrl(scheduleId),
    details: (urls, scheduleId) => urls.scheduleDetailsUrl(scheduleId),
  };

  private getScheduleUserUrl = (subspaceName?: ScheduleSubspaceName) => {
    let url = this.props.urls.scheduleUrl(this.props.spaceInfo.item.id);
    if (subspaceName) {
      const urlFn = ScheduleSpace.scheduleUrlBySubspace[subspaceName];
      url = urlFn && urlFn(this.props.urls, this.props.spaceInfo.item.id) || url;
    }
    return url;
  };

  public render() {
    const schedule = this.props.spaceInfo.item as IScheduleInfo;

    // we redirect if the subspacename doesn't match the scheduled action to the correct one
    if (this.props.spaceInfo.subspaceName !== 'details' && !scheduleSubspaceNameMatchesSchedule(this.props.spaceInfo.subspaceName as ScheduleSubspaceName, schedule.scheduledAction)) {
      return <Redirect to={schedulePath(this.props.urls, schedule.id, schedule.scheduledAction)} />;
    }

    const subspaces = this.memoizedGetSiteUserSubspaces(
      this.props.contentCounts,
      this.props.contentCountsUpToDate,
      schedule,
      this.navigateToAllSchedules,
      this.props.reloadSpace
    );

    return <TabbedSpace
      subspaces={subspaces}
      activeSubspaceName={this.props.spaceInfo.subspaceName}
      urlForSubspace={this.getScheduleUserUrl}
      mastheadElement={this.mastheadElement()}
      testIdPrefix={'schedule'}
      spaceItemName={schedule.name}
    />;
  }

  private mastheadElement(): React.ReactNode {
    return <ScheduleSpaceHeader
      item={this.props.spaceInfo.item as IScheduleInfo}
      displaySchedulesInClientTimezone={this.props.displaySchedulesInClientTimezone}
    />;
  }

  private navigateToAllSchedules = () => {
    Location.instance.setUrl(this.props.urls.schedulesUrl());
  };
}

function getScheduleSubspaces(
  contentCounts: ISpaceInfoTypeItemCount<'schedule'>,
  contentCountsUpToDate: boolean,
  schedule: IScheduleInfo,
  navigateToAllSchedules: () => void,
  reloadSpace: () => void
): ReadonlyArray<Subspace> {
  return _.compact<_.List<Subspace>>([
    schedule.scheduledAction === ScheduleActions.Extract && explorerSubspace({
      name: 'extractRefreshes',
      tabLabel: Messages.extractTasks(),
      supportedDisplayModes: ExtractTaskDefinition.supportedDisplayModes,
      supportFilterPanel: false,
      contentCount: contentCounts && contentCounts['extractRefreshes'],
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled',
    }),
    schedule.scheduledAction === ScheduleActions.Subscription && explorerSubspace({
      name: 'subscriptions',
      tabLabel: Messages.subscriptions(),
      supportedDisplayModes: SubscriptionDefinition.supportedDisplayModes,
      supportFilterPanel: false,
      contentCount: contentCounts && contentCounts['subscriptions'],
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled',
    }),
    schedule.scheduledAction === ScheduleActions.Flow && explorerSubspace({
      name: 'flows',
      tabLabel: Messages.flows(),
      supportedDisplayModes: RunFlowTaskDefinition.supportedDisplayModes,
      supportFilterPanel: false,
      contentCount: contentCounts && contentCounts['flows'],
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled'
    }),
    {
      name: 'details',
      tabLabel: Messages.details(),
      shouldRenderTabContentCount: false,
      renderContent: () => <ScheduleDetailsSubspace
        schedule={schedule}
        navigateToAllSchedules={navigateToAllSchedules}
        reloadSpace={reloadSpace}
      />
    }
  ]);
}
